import { useMemo, useState } from 'react'
import {
  useEventActions,
  useCreateAction,
  usePatchAction,
  useDeleteAction
} from '../hooks/useActions'
import { useArchived } from '../contexts/ArchiveContext'
import { Icon } from './Icon'
import { ActionEditor } from './ActionEditor'
import { QuickAddActionsPanel } from './QuickAddActionsPanel'
import type {
  EventAction,
  ActionPriority,
  ActionStatus
} from '../types'

// ActionsTab — per-event action register (spec 30). Owner-assigned
// to-do items distinct from the project-plan Gantt and the build
// checklist. MVP scope: list, add, status-toggle, delete. Inline edit
// of title / department / due / priority happens in the add modal
// reused as an edit modal (one form, two callers).
//
// Status cycle (click the badge): open → in-progress → done → open.
// Cancelled is reachable only via the modal; it deliberately doesn't
// sit on the click-cycle so an accidental third click doesn't archive
// the row.

interface Props {
  eventId: number
}

type Filter = 'all' | 'open' | 'in-progress' | 'done'

export function ActionsTab({ eventId }: Props) {
  const archived = useArchived()
  const { data, isLoading, error } = useEventActions(eventId)
  const createMut = useCreateAction(eventId)
  const patchMut = usePatchAction(eventId)
  const deleteMut = useDeleteAction(eventId)

  const [filter, setFilter] = useState<Filter>('all')
  const [editing, setEditing] = useState<EventAction | 'new' | null>(null)
  const [quickAdd, setQuickAdd] = useState(false)

  const filtered = useMemo(() => {
    if (!data) return []
    if (filter === 'all') return data
    return data.filter((a) => a.status === filter)
  }, [data, filter])

  const counts = useMemo(() => {
    const c = { all: 0, open: 0, 'in-progress': 0, done: 0 }
    for (const a of data ?? []) {
      c.all += 1
      if (a.status === 'open' || a.status === 'in-progress' || a.status === 'done') {
        c[a.status] += 1
      }
    }
    return c
  }, [data])

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading actions…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load actions</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h2 className="font-display text-xl">Actions</h2>
          <p className="text-sm text-ink-soft mt-0.5">
            Owner-assigned to-do items for this event. Click the status badge
            to cycle through open → in-progress → done.
          </p>
        </div>
        {!archived && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setQuickAdd((v) => !v)}
              className="px-3 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green transition-colors flex items-center gap-1.5"
            >
              <Icon name="list" size={14} />
              Quick add
            </button>
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="px-3 py-1.5 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 flex items-center gap-1.5"
            >
              <Icon name="plus" size={14} />
              Add action
            </button>
          </div>
        )}
      </div>

      {!archived && quickAdd && (
        <QuickAddActionsPanel eventId={eventId} onClose={() => setQuickAdd(false)} />
      )}

      <div className="flex gap-2 flex-wrap">
        <FilterPill label={`All · ${counts.all}`}            active={filter === 'all'}         onClick={() => setFilter('all')} />
        <FilterPill label={`Open · ${counts.open}`}          active={filter === 'open'}        onClick={() => setFilter('open')} />
        <FilterPill label={`In progress · ${counts['in-progress']}`} active={filter === 'in-progress'} onClick={() => setFilter('in-progress')} />
        <FilterPill label={`Done · ${counts.done}`}          active={filter === 'done'}        onClick={() => setFilter('done')} />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-line rounded p-8 text-center text-sm text-ink-soft">
          {data && data.length === 0
            ? 'No actions yet. Click “Add action” to create the first one.'
            : `No ${filter} actions.`}
        </div>
      ) : (
        <div className="bg-white border border-line rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-ink-soft border-b border-line bg-parchment/60">
                <th className="px-4 py-2">Title</th>
                <th className="px-3 py-2 w-40">Department</th>
                <th className="px-3 py-2 w-28">Due</th>
                <th className="px-3 py-2 w-20">Priority</th>
                <th className="px-3 py-2 w-28">Status</th>
                <th className="px-3 py-2 w-24">Plan</th>
                <th className="px-3 py-2 w-32">Assignee</th>
                <th className="px-3 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <ActionRow
                  key={a.id}
                  action={a}
                  archived={archived}
                  onClickStatus={(next) => patchMut.mutate({ id: a.id, body: { status: next } })}
                  onTogglePlan={() => patchMut.mutate({ id: a.id, body: { addToPlan: !a.onPlan } })}
                  onEdit={() => setEditing(a)}
                  onDelete={() => {
                    const ok = window.confirm(`Delete action "${a.title}"?`)
                    if (ok) deleteMut.mutate(a.id)
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!archived && editing && (
        <ActionEditor
          mode={editing === 'new' ? 'create' : 'edit'}
          initial={editing === 'new' ? null : editing}
          saving={createMut.isPending || patchMut.isPending}
          serverError={(createMut.error ?? patchMut.error) as Error | null}
          onClose={() => setEditing(null)}
          onSave={(body) => {
            if (editing === 'new') {
              createMut.mutate(body, { onSuccess: () => setEditing(null) })
            } else {
              patchMut.mutate(
                { id: editing.id, body },
                { onSuccess: () => setEditing(null) }
              )
            }
          }}
        />
      )}
    </div>
  )
}

// ---- Row -----------------------------------------------------------------

function ActionRow({
  action, archived, onClickStatus, onTogglePlan, onEdit, onDelete
}: {
  action: EventAction
  archived: boolean
  onClickStatus: (next: ActionStatus) => void
  onTogglePlan: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const nextStatus: ActionStatus =
    action.status === 'open'        ? 'in-progress'
    : action.status === 'in-progress' ? 'done'
    : action.status === 'done'        ? 'open'
    : action.status   // cancelled stays put

  const overdue =
    action.dueAt != null
    && action.status !== 'done'
    && action.status !== 'cancelled'
    && action.dueAt < todayIso()

  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-parchment/40 transition-colors">
      <td className="px-4 py-2.5">
        {archived ? (
          <span className="font-semibold">{action.title}</span>
        ) : (
          <button type="button" onClick={onEdit} className="text-left font-semibold hover:text-green">
            {action.title}
          </button>
        )}
        {action.description && (
          <div className="text-xs text-ink-soft mt-0.5 line-clamp-1">
            {action.description}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-ink-soft">{action.department ?? '—'}</td>
      <td className="px-3 py-2.5 text-xs font-mono">
        {action.dueAt
          ? <span className={overdue ? 'text-oxblood font-semibold' : 'text-ink-soft'}>
              {formatDueShort(action.dueAt)}
              {overdue && <span className="ml-1 text-[10px] uppercase tracking-wide">overdue</span>}
            </span>
          : <span className="text-ink-soft">—</span>}
      </td>
      <td className="px-3 py-2.5">
        <PriorityBadge priority={action.priority} />
      </td>
      <td className="px-3 py-2.5">
        {archived ? (
          <StatusBadge status={action.status} />
        ) : (
          <button
            type="button"
            onClick={() => onClickStatus(nextStatus)}
            title={`Mark as ${nextStatus}`}
            className="focus:outline-none focus:ring-2 focus:ring-green/40 rounded"
          >
            <StatusBadge status={action.status} clickable />
          </button>
        )}
      </td>
      <td className="px-3 py-2.5">
        {archived ? (
          <span className="text-[11px] text-ink-soft">{action.onPlan ? 'On plan' : '—'}</span>
        ) : (
          <button
            type="button"
            onClick={onTogglePlan}
            title={action.onPlan ? 'On the project plan — click to remove the Gantt task' : 'Add to the project plan (creates a Gantt task)'}
            className={
              'inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border transition-colors ' +
              (action.onPlan
                ? 'bg-green-soft text-green border-green/40 hover:bg-green/10'
                : 'bg-white text-ink-soft border-line hover:border-green hover:text-green')
            }
          >
            {action.onPlan ? '✓ On plan' : '+ Plan'}
          </button>
        )}
      </td>
      <td className="px-3 py-2.5">
        {action.assignees.length === 0 ? (
          <span className="text-xs text-ink-soft italic">Unassigned</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {action.assignees.map((a) => (
              <span
                key={a.id}
                title={a.name ?? undefined}
                className="inline-flex items-center text-[11px] font-semibold text-green bg-green-soft rounded-full px-2 py-0.5"
              >
                {a.name ?? `#${a.id}`}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        {!archived && (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete action"
            className="text-ink-soft hover:text-oxblood text-lg leading-none"
          >
            ×
          </button>
        )}
      </td>
    </tr>
  )
}

// ---- Badges --------------------------------------------------------------

const PRIORITY_STYLE: Record<ActionPriority, { bg: string; fg: string; label: string }> = {
  low:    { bg: '#e5eff0', fg: '#3d5a6c', label: 'Low' },
  medium: { bg: '#f5edd5', fg: '#9b7f2a', label: 'Med' },
  high:   { bg: '#f8dbdd', fg: '#7a2329', label: 'High' }
}

function PriorityBadge({ priority }: { priority: ActionPriority }) {
  const s = PRIORITY_STYLE[priority]
  return (
    <span
      className="inline-flex text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  )
}

const STATUS_STYLE: Record<ActionStatus, { bg: string; fg: string; label: string }> = {
  open:           { bg: '#ece6db', fg: '#5a5a5a', label: 'Open' },
  'in-progress':  { bg: '#fde9c8', fg: '#7a4a0e', label: 'In progress' },
  done:           { bg: '#dcebe5', fg: '#0a4a3a', label: 'Done' },
  cancelled:      { bg: '#e8e1e2', fg: '#6a4a4f', label: 'Cancelled' }
}

function StatusBadge({ status, clickable = false }: { status: ActionStatus; clickable?: boolean }) {
  const s = STATUS_STYLE[status]
  return (
    <span
      className={
        'inline-flex text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ' +
        (clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : '')
      }
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  )
}

// ---- Filter pill ---------------------------------------------------------

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-3 py-1 text-xs font-semibold rounded-full border transition-colors ' +
        (active
          ? 'bg-green text-parchment border-green'
          : 'bg-white text-ink-soft border-line hover:bg-parchment')
      }
    >
      {label}
    </button>
  )
}

// ---- Helpers -------------------------------------------------------------

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function formatDueShort(iso: string): string {
  // "2026-05-08" → "8 May"
  try {
    const d = new Date(`${iso}T00:00:00Z`)
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  } catch {
    return iso
  }
}
